import { cloneDeep } from "lodash";

import { normalizeSchemaForForm } from "components/Form/components/SchemaForm/SchemaFormInference";
import { getDefaultRequiredValue } from "components/Form/components/SchemaForm/SchemaFormService";

/**
 * Seed the editable value model of a SchemaForm from a schema, materialising the
 * SAME per-field defaults the form view renders on open.
 *
 * Why this exists (and why it differs from `getDefaultRequiredValue` at the object
 * level): a SchemaForm displays a field's default via {@link getByPath}, which
 * falls back to `getDefaultRequiredValue` ONLY while the whole value model is
 * `undefined`. The moment the model becomes a defined object (e.g. after the first
 * edit, or after seeding with a partial object), a field whose key is MISSING reads
 * back `undefined` and renders "removed"/greyed (`isRemoved = elementData ===
 * undefined && !styleRequired`). So a value model that does not already contain a
 * field's default diverges from what the form shows:
 *  - the code-view / submit payload is `null`/partial while the form shows defaults
 *    (so running unchanged drops the shown defaults), and
 *  - editing one field greys out every other field that is absent from the model.
 *
 * `getDefaultRequiredValue(schema, schema)` at the object root only seeds the
 * REQUIRED properties, so optional-with-default fields would still be absent and
 * grey out. This helper instead seeds EVERY top-level property whose default
 * resolves to a value (required and optional alike), which is exactly the set of
 * fields the form view shows as present-with-a-value on open. Nested objects are
 * seeded by `getDefaultRequiredValue` per property, which recurses required-only —
 * matching the form's per-field `getByPath(undefined)` display for nested fields,
 * so form-view, code-view and the submitted value stay consistent.
 *
 * A property with no resolvable default (e.g. a required bare dropdown with no
 * `defaultIndex`, or an optional field with no `default`) is intentionally omitted:
 * the form shows it empty too (a required one stays styled-required and must be
 * filled; an optional one keeps its "click to add" affordance), so omitting it
 * keeps the model matching the display.
 *
 * Plain JSON Schema (`type`/`properties`, no `element` keys — how flow
 * `input_schema`s and MCP tool `inputSchema`s are commonly written) is normalized
 * into the form-element dialect first, mirroring how {@link SchemaForm} renders it;
 * an already-dialect schema is left unchanged.
 */
/**
 * Resolve the value a REQUIRED/style-required `form-dropdown` renders as selected
 * on open, mirroring {@link SchemaFormDropdown}: with no data it shows the option
 * at `defaultIndex ?? 0`. Returns that option's resolved default (its `const` for a
 * const-string branch, a required-only object for a composite branch, …). Returns
 * `undefined` for a non-dropdown, an optional dropdown (which renders collapsed and
 * shows no value), a dropdown with no branches, or a branch with no resolvable
 * value — all of which the form also shows without a concrete value.
 */
const resolveShownDropdownDefault = (
    schema: any,
    propertySchema: any,
    isRequiredKey: boolean
): any => {
    if (!propertySchema || typeof propertySchema !== "object") {
        return undefined;
    }
    const isDropdown =
        propertySchema.element === "form-dropdown" ||
        propertySchema.element === "form-optional-dropdown";
    if (!isDropdown) {
        return undefined;
    }
    // Only a dropdown the form shows pre-selected (required by the object, or
    // style-required) seeds its shown option; an optional one stays "click to add".
    const isStyleRequired = propertySchema.style === "required";
    if (!isRequiredKey && !isStyleRequired) {
        return undefined;
    }
    const branches = propertySchema.oneOf ?? propertySchema.anyOf;
    if (!Array.isArray(branches) || branches.length === 0) {
        return undefined;
    }
    const index = Number.isInteger(propertySchema.defaultIndex)
        ? propertySchema.defaultIndex
        : 0;
    return getDefaultRequiredValue(schema, branches[index], null);
};

export const seedSchemaFormValues = (schema: unknown): any => {
    if (!schema) {
        return {};
    }
    const formSchema: any = normalizeSchemaForForm(schema);
    if (!formSchema || typeof formSchema !== "object") {
        return {};
    }

    const isObjectRoot =
        (formSchema.element === "form-object" ||
            formSchema.element === "form-optional-object") &&
        formSchema.properties &&
        typeof formSchema.properties === "object";

    // A non-object root (scalar/array root) cannot seed into a keyed model — fall
    // back to the single resolved default (or {} when none), matching the form's
    // whole-value display.
    if (!isObjectRoot) {
        const whole = getDefaultRequiredValue(formSchema, formSchema, null);
        return whole === undefined ? {} : whole;
    }

    const requiredKeys: Set<string> = new Set(
        Array.isArray(formSchema.required) ? formSchema.required : []
    );

    const seeded: Record<string, any> = {};
    for (const [key, propertySchema] of Object.entries(formSchema.properties)) {
        let value = getDefaultRequiredValue(formSchema, propertySchema, null);
        if (value === undefined) {
            // `getDefaultRequiredValue` resolves a dropdown's default ONLY when it
            // carries an explicit `defaultIndex`. But `SchemaFormDropdown` DISPLAYS
            // the option at `defaultIndex ?? 0` for every REQUIRED/style-required
            // dropdown whose data is still undefined (its Autocomplete value is
            // `elementData === undefined ? defaultIndex ?? 0 : selectedIndex`). So a
            // required bare dropdown (e.g. the gated flows' `env`: an anyOf of
            // const-strings with no `default`/`defaultIndex`) shows its first option
            // selected while the value model holds nothing — the exact form-view ↔
            // code-view/submit mismatch this seeding exists to kill. Materialise that
            // shown option so the model matches the display and running unchanged
            // submits it. (An OPTIONAL dropdown renders collapsed with a "click to
            // add" affordance — it shows no value — so it is intentionally left
            // unseeded.)
            value = resolveShownDropdownDefault(
                formSchema,
                propertySchema,
                requiredKeys.has(key)
            );
        }
        if (value !== undefined) {
            seeded[key] = value;
        }
    }
    // Preserve object-level additionalDefault semantics (same as the object branch
    // of getDefaultRequiredValue).
    if (formSchema.additionalDefault && typeof formSchema.additionalDefault === "object") {
        Object.assign(seeded, cloneDeep(formSchema.additionalDefault));
    }
    return seeded;
};
